import { usePeriod } from '@/app/context/PeriodContext';
import { useGetActiveBuildingsQuery } from '@/app/services/buildingsApi';
import { useFindAccountingExpensesByPeriodAndBuildingQuery } from '@/app/services/IncomingAccountingExpeseApi';
import { useFindByBuildingAndPeriodQuery } from '@/app/services/projectApi';
import ExpensesTotalTable from '@/components/buildingsTotal/ExpensesTotalTable';
import ProjectsTotalTable from '@/components/buildingsTotal/ProjectsTotalTable';
import PeriodDetails from '@/components/periodDetails/PeriodDetails';
import { MenuItem, Select } from '@mui/material';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface Data {
  id: string;
  value: number;
}
export type Order = 'asc' | 'desc';

export function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

export function getComparator<Key extends keyof any>(order: Order, orderBy: Key) {
  return order === 'desc'
    ? (a: any, b: any) => descendingComparator(a, b, orderBy)
    : (a: any, b: any) => -descendingComparator(a, b, orderBy);
}

function BuildingsTotal() {
  const { t } = useTranslation(['buildingsTotal', 'common']);
  const { selectedPeriod } = usePeriod();
  const [selectedBuilding, setSelectedBuilding] = useState(1);
  const { data: buildings } = useGetActiveBuildingsQuery();
  const { data: expenses } = useFindAccountingExpensesByPeriodAndBuildingQuery({
    period: selectedPeriod?.date,
    buildingId: selectedBuilding,
  });
  const { data: goals } = useFindByBuildingAndPeriodQuery({
    period: selectedPeriod?.date,
    buildingId: selectedBuilding,
  });

  const formatter = new Intl.NumberFormat('pt-BR');

  const [orderGoals, setOrderGoals] = React.useState<Order>('desc');
  const [orderByGoals, setOrderByGoals] = React.useState<keyof Data>('value');
  const [orderExpenses, setOrderExpenses] = React.useState<Order>('desc');
  const [orderByExpenses, setOrderByExpenses] = React.useState<keyof Data>('value');

  const handleRequestSortGoals = (property: keyof Data) => {
    const isAsc = orderByGoals === property && orderGoals === 'asc';
    setOrderGoals(isAsc ? 'desc' : 'asc');
    setOrderByGoals(property);
  };

  const handleRequestSortExpenses = (property: keyof Data) => {
    const isAsc = orderByExpenses === property && orderExpenses === 'asc';
    setOrderExpenses(isAsc ? 'desc' : 'asc');
    setOrderByExpenses(property);
  };

  const visibleGoals = React.useMemo(() => {
    if (!goals) return [];
    const stabilized = [...goals];
    return stabilized.sort((a, b) => getComparator(orderGoals, 'goal')(a, b));
  }, [goals, orderGoals]);

  const visibleExpenses = React.useMemo(() => {
    if (!expenses) return [];
    const stabilized = [...expenses];
    return stabilized.sort((a, b) => getComparator(orderExpenses, 'value')(a, b));
  }, [expenses, orderExpenses]);

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginRight: '20px',
      }}
    >
      <PeriodDetails />
      <div style={{ display: 'flex', alignItems: 'center', marginTop: '1.5rem' }}>
        <Typography variant="h1">{t('goals.expenses.total')}</Typography>
        <Typography sx={{ marginLeft: '0.5rem' }}>{`(${t('common:by.building')})`}</Typography>
      </div>
      <Typography>{t('by.building.subtitle')}</Typography>

      <div style={{ width: '100%', marginTop: '24px', marginBottom: '16px', alignItems: 'center' }}>
        <Typography variant="h2">{t('common:building')}</Typography>
        <Select
          size="small"
          sx={{ width: '25%' }}
          value={selectedBuilding}
          onChange={(e) => setSelectedBuilding(e.target.value as number)}
          renderValue={(selected) => buildings?.find((item) => item.id === selected)?.name}
        >
          {buildings?.map((building) => (
            <MenuItem key={building.name} value={building.id}>
              {building.name}
            </MenuItem>
          ))}
        </Select>
      </div>
      <div style={{ display: 'flex', width: '100%', marginTop: '24px', marginBottom: '16px' }}>
        <ProjectsTotalTable goals={goals || []} />
        <ExpensesTotalTable expenses={expenses || []} />
      </div>
    </div>
  );
}
export default BuildingsTotal;
